/**
 * Additional Details JavaScript
 * Handles vehicle occupants, third party details, additional information, and declaration
 */

// Utility function to prevent excessive function calls
function debounce(func, delay) {
    let timeoutId;
    return function (...args) {
        const context = this;
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
            func.apply(context, args);
        }, delay);
    };
}

// Occupants management
const OccupantsManager = {
    occupantsList: [],
    nextId: 1,

    init: function () {
        // Add occupant button click handler
        const addOccupantBtn = document.getElementById('add-occupant-btn');
        if (addOccupantBtn) {
            addOccupantBtn.addEventListener('click', this.showOccupantForm.bind(this));
        }

        // Cancel button click handler
        const cancelOccupantBtn = document.getElementById('cancel-occupant-btn');
        if (cancelOccupantBtn) {
            cancelOccupantBtn.addEventListener('click', this.hideOccupantForm.bind(this));
        }

        // Form submission handler
        const occupantForm = document.getElementById('occupant-form');
        if (occupantForm) {
            occupantForm.addEventListener('submit', this.handleOccupantSubmit.bind(this));
        }

        // Initialize form validation
        this.initFormValidation();

        // Update accordion status for occupants
        this.updateOccupantsAccordionStatus();

        // Bind the completion button
        this.bindCompletionButton();
    },

    showOccupantForm: function () {
        const formContainer = document.getElementById('occupant-form-container');
        if (formContainer) {
            // Show the form with a slide down effect
            formContainer.style.display = 'block';
            formContainer.style.height = '0';
            formContainer.style.overflow = 'hidden';
            formContainer.style.transition = 'height 0.3s ease-out';

            // Trigger reflow
            formContainer.offsetHeight;

            // Get the full height
            const height = formContainer.scrollHeight;
            formContainer.style.height = height + 'px';

            // Clear the height restriction after animation
            setTimeout(() => {
                formContainer.style.height = '';
                formContainer.style.overflow = '';
            }, 300);

            // Focus on the first input field
            document.getElementById('occupantFirstName').focus();
        }
    },

    hideOccupantForm: function () {
        const formContainer = document.getElementById('occupant-form-container');
        if (formContainer) {
            // Store current height
            formContainer.style.height = formContainer.scrollHeight + 'px';
            formContainer.style.overflow = 'hidden';
            formContainer.style.transition = 'height 0.3s ease-out';

            // Trigger reflow
            formContainer.offsetHeight;

            // Collapse
            formContainer.style.height = '0';

            // Hide after animation completes
            setTimeout(() => {
                formContainer.style.display = 'none';
                formContainer.style.height = '';
                formContainer.style.overflow = '';

                // Reset form
                document.getElementById('occupant-form').reset();

                // Remove validation styling
                const inputs = formContainer.querySelectorAll('input');
                inputs.forEach(input => {
                    input.classList.remove('is-invalid');
                    input.classList.remove('is-valid');
                });
            }, 300);
        }
    },

    handleOccupantSubmit: function (event) {
        event.preventDefault();

        const form = event.target;

        // Check if form is valid
        if (!form.checkValidity()) {
            event.stopPropagation();
            form.classList.add('was-validated');
            return;
        }

        // Collect form data
        const firstName = document.getElementById('occupantFirstName').value;
        const lastName = document.getElementById('occupantLastName').value;
        const phone = document.getElementById('occupantPhone').value;
        const email = document.getElementById('occupantEmail').value;

        // Create new occupant object
        const occupant = {
            id: this.nextId++,
            firstName: firstName,
            lastName: lastName,
            phone: phone,
            email: email
        };

        // Add to list
        this.occupantsList.push(occupant);

        // Update the table
        this.updateOccupantsTable();

        // Hide and reset the form
        this.hideOccupantForm();

        // Update accordion status
        this.updateOccupantsAccordionStatus();
    },

    updateOccupantsTable: function () {
        const table = document.getElementById('occupants-table');
        const tbody = table.querySelector('tbody');
        const noOccupantsRow = document.getElementById('no-occupants-row');

        // Clear existing rows except for the "no occupants" row
        const rows = tbody.querySelectorAll('tr:not(#no-occupants-row)');
        rows.forEach(row => row.remove());

        // Show/hide the "no occupants" row based on list content
        if (this.occupantsList.length === 0) {
            noOccupantsRow.style.display = '';
        } else {
            noOccupantsRow.style.display = 'none';

            // Add each occupant to the table
            this.occupantsList.forEach(occupant => {
                const row = document.createElement('tr');
                row.dataset.id = occupant.id;

                row.innerHTML = `
                    <td>${this.escapeHtml(occupant.firstName)}</td>
                    <td>${this.escapeHtml(occupant.lastName)}</td>
                    <td>${this.escapeHtml(occupant.phone)}</td>
                    <td>${this.escapeHtml(occupant.email)}</td>
                    <td class="text-center">
                        <button class="btn btn-sm btn-outline-danger delete-occupant" data-id="${occupant.id}">
                            <i class="ph ph-trash"></i>
                        </button>
                    </td>
                `;

                tbody.appendChild(row);
            });

            // Add event listeners to delete buttons
            const deleteButtons = tbody.querySelectorAll('.delete-occupant');
            deleteButtons.forEach(button => {
                button.addEventListener('click', (event) => {
                    const id = parseInt(event.currentTarget.dataset.id);
                    this.deleteOccupant(id);
                });
            });
        }
    },

    markSectionComplete: function () {
        console.log("Marking occupants section complete"); // Add for debugging
        // Update accordion status
        if (window.ClaimAssistant && typeof ClaimAssistant.updateAccordionStatus === 'function') {
            ClaimAssistant.updateAccordionStatus('collapseSix', 'done');

            // Collapse the accordion after a short delay
            setTimeout(() => {
                const accordionButton = document.querySelector('button[data-bs-target="#collapseSix"]');
                if (accordionButton && !accordionButton.classList.contains('collapsed')) {
                    accordionButton.click();
                }
            }, 500);
        } else {
            console.error("ClaimAssistant or updateAccordionStatus method not found");
        }
    },

    bindCompletionButton: function () {
        const markCompleteBtn = document.getElementById('mark-occupants-done-btn');
        if (markCompleteBtn) {
            markCompleteBtn.addEventListener('click', () => {
                // Force status to "done" regardless of occupant list
                if (window.ClaimAssistant && typeof ClaimAssistant.updateAccordionStatus === 'function') {
                    ClaimAssistant.updateAccordionStatus('collapseSix', 'done');

                    // Collapse the accordion
                    setTimeout(() => {
                        const accordionButton = document.querySelector('button[data-bs-target="#collapseSix"]');
                        if (accordionButton && !accordionButton.classList.contains('collapsed')) {
                            accordionButton.click();
                        }
                    }, 300);
                }
            });
        }
    },

    deleteOccupant: function (id) {
        // Filter out the occupant with the matching id
        this.occupantsList = this.occupantsList.filter(occupant => occupant.id !== id);

        // Update the table
        this.updateOccupantsTable();

        // Update accordion status
        this.updateOccupantsAccordionStatus();
    },

    initFormValidation: function () {
        // Phone number validation
        const phoneInput = document.getElementById('occupantPhone');
        if (phoneInput) {
            phoneInput.addEventListener('input', function (e) {
                const value = e.target.value.replace(/\D/g, '');
                const isValid = value.length >= 10;

                if (value) {
                    e.target.classList.toggle('is-valid', isValid);
                    e.target.classList.toggle('is-invalid', !isValid);
                } else {
                    e.target.classList.remove('is-valid', 'is-invalid');
                }
            });
        }

        // Email validation
        const emailInput = document.getElementById('occupantEmail');
        if (emailInput) {
            emailInput.addEventListener('input', function (e) {
                const value = e.target.value;
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                const isValid = emailRegex.test(value);

                if (value) {
                    e.target.classList.toggle('is-valid', isValid);
                    e.target.classList.toggle('is-invalid', !isValid);
                } else {
                    e.target.classList.remove('is-valid', 'is-invalid');
                }
            });
        }
    },

    updateOccupantsAccordionStatus: function () {
        // Check if ClaimAssistant exists and has the updateAccordionStatus method
        if (window.ClaimAssistant && typeof ClaimAssistant.updateAccordionStatus === 'function') {
            // Update accordion status based on occupants list
            if (this.occupantsList.length > 0) {
                ClaimAssistant.updateAccordionStatus('collapseSix', 'done');
            } else {
                ClaimAssistant.updateAccordionStatus('collapseSix', 'pending');
            }
        }
    },

    // Helper method to safely escape HTML
    escapeHtml: function (unsafe) {
        return unsafe
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");
    }
};

// Third Party Management
const ThirdPartyManager = {
    thirdPartyList: [],
    nextId: 1,
    // Vehicle makes, models, years, and colors
    vehicleDetails: {
        makes: ['Ford', 'Toyota', 'BMW', 'Audi', 'Volkswagen', 'Mercedes', 'Vauxhall', 'Nissan'],
        models: {
            'Ford': ['Focus', 'Fiesta', 'Mondeo', 'Kuga'],
            'Toyota': ['Corolla', 'Yaris', 'RAV4', 'Prius'],
            'BMW': ['1 Series', '3 Series', '5 Series', 'X5'],
            'Audi': ['A1', 'A3', 'A4', 'Q5'],
            'Volkswagen': ['Golf', 'Polo', 'Tiguan', 'Passat'],
            'Mercedes': ['A-Class', 'C-Class', 'E-Class', 'GLA'],
            'Vauxhall': ['Corsa', 'Astra', 'Insignia', 'Mokka'],
            'Nissan': ['Qashqai', 'Juke', 'Micra', 'Leaf']
        },
        years: ['2018', '2019', '2020', '2021', '2022', '2023'],
        colors: ['Black', 'White', 'Blue', 'Red', 'Silver', 'Grey', 'Green']
    },

    init: function () {
        // Add third party button click handler
        const addThirdPartyBtn = document.getElementById('add-third-party-btn');
        if (addThirdPartyBtn) {
            addThirdPartyBtn.addEventListener('click', this.showThirdPartyForm.bind(this));
        }

        // Cancel button click handler
        const cancelThirdPartyBtn = document.getElementById('cancel-third-party-btn');
        if (cancelThirdPartyBtn) {
            cancelThirdPartyBtn.addEventListener('click', this.hideThirdPartyForm.bind(this));
        }

        // Form submission handler
        const thirdPartyForm = document.getElementById('third-party-form');
        if (thirdPartyForm) {
            thirdPartyForm.addEventListener('submit', this.handleThirdPartySubmit.bind(this));
        }

        // VRN lookup button handler
        const lookupVrnBtn = document.getElementById('lookup-vrn-btn');
        if (lookupVrnBtn) {
            lookupVrnBtn.addEventListener('click', this.lookupVRN.bind(this));
        }

        // VRN input handler for real-time validation
        const vrnInput = document.getElementById('thirdPartyVRN');
        if (vrnInput) {
            vrnInput.addEventListener('input', this.validateVRN.bind(this));
        }

        // Initialize form validation
        this.initFormValidation();

        // Update accordion status for third parties
        this.updateThirdPartyAccordionStatus();

        // Bind the completion button
        this.bindCompletionButton();
    },

    showThirdPartyForm: function () {
        const formContainer = document.getElementById('third-party-form-container');
        if (formContainer) {
            // Show the form with a slide down effect
            formContainer.style.display = 'block';
            formContainer.style.height = '0';
            formContainer.style.overflow = 'hidden';
            formContainer.style.transition = 'height 0.3s ease-out';

            // Trigger reflow
            formContainer.offsetHeight;

            // Get the full height
            const height = formContainer.scrollHeight;
            formContainer.style.height = height + 'px';

            // Clear the height restriction after animation
            setTimeout(() => {
                formContainer.style.height = '';
                formContainer.style.overflow = '';
            }, 300);

            // Focus on the VRN input field
            document.getElementById('thirdPartyVRN').focus();
        }
    },

    hideThirdPartyForm: function () {
        const formContainer = document.getElementById('third-party-form-container');
        if (formContainer) {
            // Store current height
            formContainer.style.height = formContainer.scrollHeight + 'px';
            formContainer.style.overflow = 'hidden';
            formContainer.style.transition = 'height 0.3s ease-out';

            // Trigger reflow
            formContainer.offsetHeight;

            // Collapse
            formContainer.style.height = '0';

            // Hide after animation completes
            setTimeout(() => {
                formContainer.style.display = 'none';
                formContainer.style.height = '';
                formContainer.style.overflow = '';

                // Reset form
                document.getElementById('third-party-form').reset();

                // Clear lookup result
                const lookupResult = document.getElementById('vrn-lookup-result');
                if (lookupResult) {
                    lookupResult.innerHTML = '';
                }

                // Remove validation styling
                const inputs = formContainer.querySelectorAll('input');
                inputs.forEach(input => {
                    input.classList.remove('is-invalid');
                    input.classList.remove('is-valid');
                });
            }, 300);
        }
    },

    validateVRN: function (event) {
        const vrnInput = event.target;
        const value = vrnInput.value.toUpperCase();

        // UK VRN format validation
        // Current format (2001 onwards): AB12 ABC or AB12ABC
        // Older format: A123 ABC or A123ABC
        // Even older format: ABC 123A or ABC123A
        // Personalized: AB12 or AB12A
        const ukVrnRegex = /^([A-Z]{2}[0-9]{2}[A-Z]{3}|[A-Z]{2}[0-9]{2}\s[A-Z]{3}|[A-Z][0-9]{3}[A-Z]{3}|[A-Z][0-9]{3}\s[A-Z]{3}|[A-Z]{3}[0-9]{3}[A-Z]|[A-Z]{3}\s[0-9]{3}[A-Z]|[A-Z]{1,3}[0-9]{1,3}[A-Z]{0,3})$/;

        const isValid = ukVrnRegex.test(value);

        if (value) {
            vrnInput.classList.toggle('is-valid', isValid);
            vrnInput.classList.toggle('is-invalid', !isValid);
        } else {
            vrnInput.classList.remove('is-valid', 'is-invalid');
        }

        // Clear any previous lookup results when the input changes
        const lookupResult = document.getElementById('vrn-lookup-result');
        if (lookupResult) {
            lookupResult.innerHTML = '';
        }
    },

    // Update these two methods in the ThirdPartyManager object

    lookupVRN: function () {
        const vrnInput = document.getElementById('thirdPartyVRN');
        const lookupResult = document.getElementById('vrn-lookup-result');

        if (!vrnInput || !lookupResult) return;

        const vrn = vrnInput.value.toUpperCase();

        // Clear previous result
        lookupResult.innerHTML = '';

        // UK VRN regex
        const ukVrnRegex = /^([A-Z]{2}[0-9]{2}[A-Z]{3}|[A-Z]{2}[0-9]{2}\s[A-Z]{3}|[A-Z][0-9]{3}[A-Z]{3}|[A-Z][0-9]{3}\s[A-Z]{3}|[A-Z]{3}[0-9]{3}[A-Z]|[A-Z]{3}\s[0-9]{3}[A-Z]|[A-Z]{1,3}[0-9]{1,3}[A-Z]{0,3})$/;

        // Check if the VRN is valid
        if (!ukVrnRegex.test(vrn)) {
            // Show validation error
            lookupResult.innerHTML = `
            <div class="alert alert-danger mt-2 mb-0 py-2">
                <small>
                    <strong>Invalid registration number.</strong> Please enter a valid UK vehicle registration.
                </small>
            </div>
        `;

            // Mark input as invalid
            vrnInput.classList.add('is-invalid');
            vrnInput.classList.remove('is-valid');
            return;
        }

        // Show loading message
        lookupResult.innerHTML = '<span class="text-muted"><i class="ph ph-spinner ph-spin me-2"></i>Looking up vehicle details...</span>';

        // Simulate API call with timeout
        setTimeout(() => {
            // Generate random vehicle details
            const randomVehicle = this.generateRandomVehicle();

            // Display the random vehicle information
            lookupResult.innerHTML = `
            <div class="alert alert-success mt-2 mb-0 py-2">
                <small>
                    <strong>Vehicle found:</strong> ${randomVehicle.year} ${randomVehicle.make} ${randomVehicle.model}, ${randomVehicle.color}
                </small>
            </div>
        `;

            // Mark input as valid
            vrnInput.classList.add('is-valid');
            vrnInput.classList.remove('is-invalid');

        }, 1000); // 1 second delay to simulate API call
    },

    generateRandomVehicle: function () {
        // Randomly select a make
        const make = this.vehicleDetails.makes[Math.floor(Math.random() * this.vehicleDetails.makes.length)];

        // Get models for the selected make and choose one randomly
        const models = this.vehicleDetails.models[make];
        const model = models[Math.floor(Math.random() * models.length)];

        // Select a random year and color
        const year = this.vehicleDetails.years[Math.floor(Math.random() * this.vehicleDetails.years.length)];
        const color = this.vehicleDetails.colors[Math.floor(Math.random() * this.vehicleDetails.colors.length)];

        return {
            make,
            model,
            year,
            color
        };
    },

    handleThirdPartySubmit: function (event) {
        event.preventDefault();

        const form = event.target;

        // Check if form is valid
        if (!form.checkValidity()) {
            event.stopPropagation();
            form.classList.add('was-validated');
            return;
        }

        // Collect form data
        const vrn = document.getElementById('thirdPartyVRN').value.toUpperCase();
        const firstName = document.getElementById('thirdPartyFirstName').value;
        const lastName = document.getElementById('thirdPartyLastName').value;
        const phone = document.getElementById('thirdPartyPhone').value;
        const email = document.getElementById('thirdPartyEmail').value;

        // Create new third party object
        const thirdParty = {
            id: this.nextId++,
            vrn: vrn,
            firstName: firstName,
            lastName: lastName,
            phone: phone,
            email: email
        };

        // Add to list
        this.thirdPartyList.push(thirdParty);

        // Update the table
        this.updateThirdPartyTable();

        // Hide and reset the form
        this.hideThirdPartyForm();

        // Update accordion status
        this.updateThirdPartyAccordionStatus();
    },

    updateThirdPartyTable: function () {
        const table = document.getElementById('third-parties-table');
        const tbody = table.querySelector('tbody');
        const noThirdPartiesRow = document.getElementById('no-third-parties-row');

        // Clear existing rows except for the "no third parties" row
        const rows = tbody.querySelectorAll('tr:not(#no-third-parties-row)');
        rows.forEach(row => row.remove());

        // Show/hide the "no third parties" row based on list content
        if (this.thirdPartyList.length === 0) {
            noThirdPartiesRow.style.display = '';
        } else {
            noThirdPartiesRow.style.display = 'none';

            // Add each third party to the table
            this.thirdPartyList.forEach(thirdParty => {
                const row = document.createElement('tr');
                row.dataset.id = thirdParty.id;

                row.innerHTML = `
                    <td>${this.escapeHtml(thirdParty.vrn)}</td>
                    <td>${this.escapeHtml(thirdParty.firstName)}</td>
                    <td>${this.escapeHtml(thirdParty.lastName)}</td>
                    <td>${this.escapeHtml(thirdParty.phone)}</td>
                    <td>${this.escapeHtml(thirdParty.email)}</td>
                    <td class="text-center">
                        <button class="btn btn-sm btn-outline-danger delete-third-party" data-id="${thirdParty.id}">
                            <i class="ph ph-trash"></i>
                        </button>
                    </td>
                `;

                tbody.appendChild(row);
            });

            // Add event listeners to delete buttons
            const deleteButtons = tbody.querySelectorAll('.delete-third-party');
            deleteButtons.forEach(button => {
                button.addEventListener('click', (event) => {
                    const id = parseInt(event.currentTarget.dataset.id);
                    this.deleteThirdParty(id);
                });
            });
        }
    },

    deleteThirdParty: function (id) {
        // Filter out the third party with the matching id
        this.thirdPartyList = this.thirdPartyList.filter(thirdParty => thirdParty.id !== id);

        // Update the table
        this.updateThirdPartyTable();

        // Update accordion status
        this.updateThirdPartyAccordionStatus();
    },

    markSectionComplete: function () {
        console.log("Marking occupants section complete"); // Add for debugging
        // Update accordion status
        if (window.ClaimAssistant && typeof ClaimAssistant.updateAccordionStatus === 'function') {
            ClaimAssistant.updateAccordionStatus('collapseSeven', 'done');

            // Collapse the accordion after a short delay
            setTimeout(() => {
                const accordionButton = document.querySelector('button[data-bs-target="#collapseSeven"]');
                if (accordionButton && !accordionButton.classList.contains('collapsed')) {
                    accordionButton.click();
                }
            }, 500);
        } else {
            console.error("ClaimAssistant or updateAccordionStatus method not found");
        }
    },

    bindCompletionButton: function () {
        const markCompleteBtn = document.getElementById('mark-third-party-done-btn');
        if (markCompleteBtn) {
            markCompleteBtn.addEventListener('click', () => {
                // Force status to "done" regardless of third party list
                if (window.ClaimAssistant && typeof ClaimAssistant.updateAccordionStatus === 'function') {
                    ClaimAssistant.updateAccordionStatus('collapseSeven', 'done');

                    // Collapse the accordion
                    setTimeout(() => {
                        const accordionButton = document.querySelector('button[data-bs-target="#collapseSeven"]');
                        if (accordionButton && !accordionButton.classList.contains('collapsed')) {
                            accordionButton.click();
                        }
                    }, 300);
                }
            });
        }
    },

    initFormValidation: function () {
        // Phone number validation
        const phoneInput = document.getElementById('thirdPartyPhone');
        if (phoneInput) {
            phoneInput.addEventListener('input', function (e) {
                const value = e.target.value.replace(/\D/g, '');
                const isValid = value.length >= 10;

                if (value) {
                    e.target.classList.toggle('is-valid', isValid);
                    e.target.classList.toggle('is-invalid', !isValid);
                } else {
                    e.target.classList.remove('is-valid', 'is-invalid');
                }
            });
        }

        // Email validation
        const emailInput = document.getElementById('thirdPartyEmail');
        if (emailInput) {
            emailInput.addEventListener('input', function (e) {
                const value = e.target.value;
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                const isValid = emailRegex.test(value);

                if (value) {
                    e.target.classList.toggle('is-valid', isValid);
                    e.target.classList.toggle('is-invalid', !isValid);
                } else {
                    e.target.classList.remove('is-valid', 'is-invalid');
                }
            });
        }
    },

    updateThirdPartyAccordionStatus: function () {
        // Check if ClaimAssistant exists and has the updateAccordionStatus method
        if (window.ClaimAssistant && typeof ClaimAssistant.updateAccordionStatus === 'function') {
            // Update accordion status based on third party list
            if (this.thirdPartyList.length > 0) {
                ClaimAssistant.updateAccordionStatus('collapseSeven', 'done');
            } else {
                ClaimAssistant.updateAccordionStatus('collapseSeven', 'pending');
            }
        }
    },

    // Helper method to safely escape HTML
    escapeHtml: function (unsafe) {
        return unsafe
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");
    }
};

// Witness Management
const WitnessManager = {
    witnessList: [],
    nextId: 1,

    init: function () {
        // Add witness button click handler
        const addWitnessBtn = document.getElementById('add-witness-btn');
        if (addWitnessBtn) {
            addWitnessBtn.addEventListener('click', this.showWitnessForm.bind(this));
        }

        // Cancel button click handler
        const cancelWitnessBtn = document.getElementById('cancel-witness-btn');
        if (cancelWitnessBtn) {
            cancelWitnessBtn.addEventListener('click', this.hideWitnessForm.bind(this));
        }

        // Form submission handler
        const witnessForm = document.getElementById('witness-form');
        if (witnessForm) {
            witnessForm.addEventListener('submit', this.handleWitnessSubmit.bind(this));
        }

        // Initialize form validation
        this.initFormValidation();

        // Update accordion status
        this.updateWitnessAccordionStatus();

        // Bind the completion button
        this.bindCompletionButton();
    },

    showWitnessForm: function () {
        const formContainer = document.getElementById('witness-form-container');
        if (formContainer) {
            // Show the form with a slide down effect
            formContainer.style.display = 'block';
            formContainer.style.height = '0';
            formContainer.style.overflow = 'hidden';
            formContainer.style.transition = 'height 0.3s ease-out';

            // Trigger reflow
            formContainer.offsetHeight;

            // Get the full height
            const height = formContainer.scrollHeight;
            formContainer.style.height = height + 'px';

            // Clear the height restriction after animation
            setTimeout(() => {
                formContainer.style.height = '';
                formContainer.style.overflow = '';
            }, 300);

            // Focus on the first input field
            document.getElementById('witnessName').focus();
        }
    },

    hideWitnessForm: function () {
        const formContainer = document.getElementById('witness-form-container');
        if (formContainer) {
            // Store current height
            formContainer.style.height = formContainer.scrollHeight + 'px';
            formContainer.style.overflow = 'hidden';
            formContainer.style.transition = 'height 0.3s ease-out';

            // Trigger reflow
            formContainer.offsetHeight;

            // Collapse
            formContainer.style.height = '0';

            // Hide after animation completes
            setTimeout(() => {
                formContainer.style.display = 'none';
                formContainer.style.height = '';
                formContainer.style.overflow = '';

                // Reset form
                document.getElementById('witness-form').reset();

                // Remove validation styling
                const inputs = formContainer.querySelectorAll('input');
                inputs.forEach(input => {
                    input.classList.remove('is-invalid');
                    input.classList.remove('is-valid');
                });
            }, 300);
        }
    },

    handleWitnessSubmit: function (event) {
        event.preventDefault();

        const form = event.target;

        // Check if form is valid
        if (!form.checkValidity()) {
            event.stopPropagation();
            form.classList.add('was-validated');
            return;
        }

        // Collect form data
        const name = document.getElementById('witnessName').value;
        const phone = document.getElementById('witnessPhone').value;

        // Create new witness object
        const witness = {
            id: this.nextId++,
            name: name,
            phone: phone
        };

        // Add to list
        this.witnessList.push(witness);

        // Update the table
        this.updateWitnessTable();

        // Hide and reset the form
        this.hideWitnessForm();

        // Update accordion status
        this.updateWitnessAccordionStatus();
    },

    updateWitnessTable: function () {
        const table = document.getElementById('witnesses-table');
        const tbody = table.querySelector('tbody');
        const noWitnessesRow = document.getElementById('no-witnesses-row');

        // Clear existing rows except for the "no witnesses" row
        const rows = tbody.querySelectorAll('tr:not(#no-witnesses-row)');
        rows.forEach(row => row.remove());

        // Show/hide the "no witnesses" row based on list content
        if (this.witnessList.length === 0) {
            noWitnessesRow.style.display = '';
        } else {
            noWitnessesRow.style.display = 'none';

            // Add each witness to the table
            this.witnessList.forEach(witness => {
                const row = document.createElement('tr');
                row.dataset.id = witness.id;

                row.innerHTML = `
                    <td>${this.escapeHtml(witness.name)}</td>
                    <td>${this.escapeHtml(witness.phone)}</td>
                    <td class="text-center">
                        <button class="btn btn-sm btn-outline-danger delete-witness" data-id="${witness.id}">
                            <i class="ph ph-trash"></i>
                        </button>
                    </td>
                `;

                tbody.appendChild(row);
            });

            // Add event listeners to delete buttons
            const deleteButtons = tbody.querySelectorAll('.delete-witness');
            deleteButtons.forEach(button => {
                button.addEventListener('click', (event) => {
                    const id = parseInt(event.currentTarget.dataset.id);
                    this.deleteWitness(id);
                });
            });
        }
    },

    deleteWitness: function (id) {
        // Filter out the witness with the matching id
        this.witnessList = this.witnessList.filter(witness => witness.id !== id);

        // Update the table
        this.updateWitnessTable();

        // Update accordion status
        this.updateWitnessAccordionStatus();
    },

    markSectionComplete: function () {
        console.log("Marking witness section complete"); // For debugging
        // Update accordion status
        if (window.ClaimAssistant && typeof ClaimAssistant.updateAccordionStatus === 'function') {
            ClaimAssistant.updateAccordionStatus('collapseEight', 'done');

            // Collapse the accordion after a short delay
            setTimeout(() => {
                const accordionButton = document.querySelector('button[data-bs-target="#collapseEight"]');
                if (accordionButton && !accordionButton.classList.contains('collapsed')) {
                    accordionButton.click();
                }
            }, 500);
        } else {
            console.error("ClaimAssistant or updateAccordionStatus method not found");
        }
    },

    bindCompletionButton: function () {
        const markCompleteBtn = document.getElementById('mark-witness-done-btn');
        if (markCompleteBtn) {
            markCompleteBtn.addEventListener('click', () => {
                // Force status to "done" regardless of witness list
                if (window.ClaimAssistant && typeof ClaimAssistant.updateAccordionStatus === 'function') {
                    ClaimAssistant.updateAccordionStatus('collapseEight', 'done');

                    // Collapse the accordion
                    setTimeout(() => {
                        const accordionButton = document.querySelector('button[data-bs-target="#collapseEight"]');
                        if (accordionButton && !accordionButton.classList.contains('collapsed')) {
                            accordionButton.click();
                        }
                    }, 300);
                }
            });
        }
    },

    initFormValidation: function () {
        // Phone number validation
        const phoneInput = document.getElementById('witnessPhone');
        if (phoneInput) {
            phoneInput.addEventListener('input', function (e) {
                const value = e.target.value.replace(/\D/g, '');
                const isValid = value.length >= 10;

                if (value) {
                    e.target.classList.toggle('is-valid', isValid);
                    e.target.classList.toggle('is-invalid', !isValid);
                } else {
                    e.target.classList.remove('is-valid', 'is-invalid');
                }
            });
        }
    },

    updateWitnessAccordionStatus: function () {
        // Check if ClaimAssistant exists and has the updateAccordionStatus method
        if (window.ClaimAssistant && typeof ClaimAssistant.updateAccordionStatus === 'function') {
            // Update accordion status based on witness list
            if (this.witnessList.length > 0) {
                ClaimAssistant.updateAccordionStatus('collapseEight', 'done');
            } else {
                ClaimAssistant.updateAccordionStatus('collapseEight', 'pending');
            }
        }
    },

    // Helper method to safely escape HTML
    escapeHtml: function (unsafe) {
        return unsafe
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");
    }
};

// Document Upload Management
const DocumentManager = {
    uploadedFiles: [],
    fileCategories: [
        "CCTV footage",
        "Dashcam footage",
        "Damage photos",
        "Tachograph data",
        "Witness statement",
        "Police report",
        "Repair estimate",
        "Other"
    ],

    init: function () {
        // Handle file uploads
        const fileInput = document.getElementById('documentUpload');
        if (fileInput) {
            fileInput.addEventListener('change', this.handleFileUpload.bind(this));
        }

        // Handle mark complete button
        const markCompleteBtn = document.getElementById('mark-documents-done-btn');
        if (markCompleteBtn) {
            markCompleteBtn.addEventListener('click', this.markSectionComplete.bind(this));
        }
    },

    handleFileUpload: function (event) {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        // Process each file
        for (let i = 0; i < files.length; i++) {
            const file = files[i];

            // Create a unique id for the file
            const fileId = Date.now() + '-' + i;

            // Add to our internal list
            this.uploadedFiles.push({
                id: fileId,
                file: file,
                name: file.name,
                size: this.formatFileSize(file.size),
                type: file.type,
                category: this.guessCategory(file.name, file.type),
                customName: ''
            });
        }

        // Update UI
        this.updateFileDisplay();

        // Update accordion status
        this.updateDocumentsAccordionStatus();

        // Reset the file input so the same file can be selected again if needed
        event.target.value = '';
    },

    formatFileSize: function (bytes) {
        if (bytes < 1024) return bytes + " bytes";
        else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
        else return (bytes / 1048576).toFixed(1) + " MB";
    },

    guessCategory: function (fileName, fileType) {
        fileName = fileName.toLowerCase();

        if (fileType.startsWith('video/')) {
            if (fileName.includes('dash') || fileName.includes('cam')) {
                return "Dashcam footage";
            } else if (fileName.includes('cctv')) {
                return "CCTV footage";
            } else {
                return "Dashcam footage"; // Default for video
            }
        } else if (fileType.startsWith('image/')) {
            if (fileName.includes('damage') || fileName.includes('crash')) {
                return "Damage photos";
            } else {
                return "Damage photos"; // Default for images
            }
        } else if (fileName.includes('tacho') || fileName.includes('log')) {
            return "Tachograph data";
        } else if (fileName.includes('police') || fileName.includes('report')) {
            return "Police report";
        } else if (fileName.includes('statement') || fileName.includes('witness')) {
            return "Witness statement";
        } else if (fileName.includes('estimate') || fileName.includes('repair') || fileName.includes('quote')) {
            return "Repair estimate";
        }

        return "Other";
    },

    updateFileDisplay: function () {
        const filePreviewsContainer = document.getElementById('file-previews');
        const noFilesMessage = document.getElementById('no-files-message');

        if (!filePreviewsContainer || !noFilesMessage) return;

        // Clear existing previews
        filePreviewsContainer.innerHTML = '';

        // Show/hide the no files message
        if (this.uploadedFiles.length === 0) {
            noFilesMessage.style.display = 'block';
        } else {
            noFilesMessage.style.display = 'none';

            // Add each file
            this.uploadedFiles.forEach(fileData => {
                const fileCard = document.createElement('div');
                fileCard.className = 'col-md-4 mb-3';
                fileCard.dataset.fileId = fileData.id;

                let thumbnailHtml = '';

                if (fileData.type.startsWith('image/')) {
                    // Create image thumbnail
                    const thumbnail = URL.createObjectURL(fileData.file);
                    thumbnailHtml = `
                        <div class="text-center mb-2">
                            <img src="${thumbnail}" class="img-thumbnail" style="max-height: 120px; max-width: 100%;" alt="Preview">
                        </div>
                    `;
                } else if (fileData.type.startsWith('video/')) {
                    // Video icon
                    thumbnailHtml = `
                        <div class="text-center mb-2">
                            <i class="ph ph-video fs-1 text-secondary"></i>
                            <div class="mt-1 text-muted">Video file</div>
                        </div>
                    `;
                } else if (fileData.type.includes('pdf')) {
                    // PDF icon
                    thumbnailHtml = `
                        <div class="text-center mb-2">
                            <i class="ph ph-file-pdf fs-1 text-danger"></i>
                            <div class="mt-1 text-muted">PDF document</div>
                        </div>
                    `;
                } else {
                    // Generic file icon
                    thumbnailHtml = `
                        <div class="text-center mb-2">
                            <i class="ph ph-file fs-1 text-primary"></i>
                            <div class="mt-1 text-muted">File</div>
                        </div>
                    `;
                }

                // Build file card with category selection and custom name
                fileCard.innerHTML = `
                    <div class="card h-100">
                        <div class="card-body">
                            ${thumbnailHtml}
                            <h6 class="card-title text-truncate" title="${fileData.name}">${fileData.name}</h6>
                            <p class="card-text small text-muted">${fileData.size}</p>
                            
                            <div class="mb-2">
                                <label class="form-label small">Category:</label>
                                <select class="form-select form-select-sm file-category" data-file-id="${fileData.id}">
                                    ${this.fileCategories.map(category =>
                    `<option value="${category}" ${category === fileData.category ? 'selected' : ''}>${category}</option>`
                ).join('')}
                                </select>
                            </div>
                            
                            <div class="mb-2">
                                <label class="form-label small">Custom Name:</label>
                                <input type="text" class="form-control form-control-sm file-custom-name" 
                                    data-file-id="${fileData.id}" placeholder="Optional description"
                                    value="${fileData.customName}">
                            </div>
                        </div>
                        <div class="card-footer bg-transparent border-top-0 text-end">
                            <button class="btn btn-sm btn-outline-danger delete-file" data-file-id="${fileData.id}">
                                <i class="ph ph-trash"></i> Remove
                            </button>
                        </div>
                    </div>
                `;

                filePreviewsContainer.appendChild(fileCard);
            });

            // Add event listeners to the newly added elements
            this.addFileCardEventListeners();
        }
    },

    addFileCardEventListeners: function () {
        // Category change
        document.querySelectorAll('.file-category').forEach(select => {
            select.addEventListener('change', (event) => {
                const fileId = event.target.dataset.fileId;
                const fileObj = this.uploadedFiles.find(f => f.id === fileId);
                if (fileObj) {
                    fileObj.category = event.target.value;
                }
            });
        });

        // Custom name change
        document.querySelectorAll('.file-custom-name').forEach(input => {
            input.addEventListener('input', (event) => {
                const fileId = event.target.dataset.fileId;
                const fileObj = this.uploadedFiles.find(f => f.id === fileId);
                if (fileObj) {
                    fileObj.customName = event.target.value;
                }
            });
        });

        // Delete file
        document.querySelectorAll('.delete-file').forEach(button => {
            button.addEventListener('click', (event) => {
                const fileId = event.target.closest('.delete-file').dataset.fileId;
                this.deleteFile(fileId);
            });
        });
    },

    deleteFile: function (fileId) {
        // Remove the file from our array
        this.uploadedFiles = this.uploadedFiles.filter(f => f.id !== fileId);

        // Update UI
        this.updateFileDisplay();

        // Update accordion status
        this.updateDocumentsAccordionStatus();
    },

    markSectionComplete: function () {
        console.log("Marking documents section complete");
        // Update accordion status
        if (window.ClaimAssistant && typeof ClaimAssistant.updateAccordionStatus === 'function') {
            ClaimAssistant.updateAccordionStatus('collapseEleven', 'done');

            // Collapse the accordion after a short delay
            setTimeout(() => {
                const accordionButton = document.querySelector('button[data-bs-target="#collapseEleven"]');
                if (accordionButton && !accordionButton.classList.contains('collapsed')) {
                    accordionButton.click();
                }
            }, 500);
        } else {
            console.error("ClaimAssistant or updateAccordionStatus method not found");
        }
    },

    updateDocumentsAccordionStatus: function () {
        // Check if ClaimAssistant exists and has the updateAccordionStatus method
        if (window.ClaimAssistant && typeof ClaimAssistant.updateAccordionStatus === 'function') {
            // Update accordion status based on whether files have been uploaded
            if (this.uploadedFiles.length > 0) {
                ClaimAssistant.updateAccordionStatus('collapseEleven', 'done');
            } else {
                ClaimAssistant.updateAccordionStatus('collapseEleven', 'pending');
            }
        }
    }
};

// Additional Information section
const AdditionalInfoManager = {
    init: function () {
        // Get the additional notes textarea
        const additionalNotes = document.getElementById('additionalNotes');
        if (additionalNotes) {
            additionalNotes.addEventListener('input', debounce(this.updateAdditionalInfoStatus.bind(this), 500));
        }
    },

    updateAdditionalInfoStatus: function () {
        // Check if ClaimAssistant exists and has the updateAccordionStatus method
        if (window.ClaimAssistant && typeof ClaimAssistant.updateAccordionStatus === 'function') {
            // Get the notes field
            const additionalNotes = document.getElementById('additionalNotes');
            if (additionalNotes) {
                // Update accordion status based on whether notes have been entered
                const hasNotes = additionalNotes.value.trim().length > 0;
                ClaimAssistant.updateAccordionStatus('collapseNine', hasNotes ? 'done' : 'pending');
            }
        }
    }
};

// Declaration management
const DeclarationManager = {
    init: function () {
        // Get the declaration checkbox
        const declarationCheck = document.getElementById('declarationCheck');
        if (declarationCheck) {
            declarationCheck.addEventListener('change', this.updateDeclarationStatus.bind(this));
        }
    },

    updateDeclarationStatus: function (event) {
        // Check if ClaimAssistant exists and has the updateAccordionStatus method
        if (window.ClaimAssistant && typeof ClaimAssistant.updateAccordionStatus === 'function') {
            // Update accordion status based on checkbox state
            const isChecked = event.target.checked;
            ClaimAssistant.updateAccordionStatus('collapseTen', isChecked ? 'done' : 'review');
        }
    }
};

// Initialize everything when the DOM is ready
document.addEventListener('DOMContentLoaded', function () {
    // Then initialize all managers
    OccupantsManager.init();
    ThirdPartyManager.init();
    AdditionalInfoManager.init();
    DeclarationManager.init();
    WitnessManager.init();
    DocumentManager.init();

    // Add functionality to show the left border on completed accordion items
    function updateAccordionItemAppearance() {
        // Check if ClaimAssistant exists and has the accordionStatus property
        if (window.ClaimAssistant && ClaimAssistant.accordionStatus) {
            // Loop through all the accordion statuses
            Object.keys(ClaimAssistant.accordionStatus).forEach(sectionId => {
                const status = ClaimAssistant.accordionStatus[sectionId];
                const accordionItem = document.querySelector(`#${sectionId}`).closest('.accordion-item');

                // Remove any existing status classes
                accordionItem.classList.remove('completed', 'in-review');

                // Add the appropriate class based on the status
                if (status === 'done') {
                    accordionItem.classList.add('completed');
                } else if (status === 'review') {
                    accordionItem.classList.add('in-review');
                }
            });
        }
    }

    // Hook this function into the ClaimAssistant.updateAccordionStatus method
    if (window.ClaimAssistant) {
        const originalUpdateAccordionStatus = ClaimAssistant.updateAccordionStatus;
        ClaimAssistant.updateAccordionStatus = function (sectionId, status) {
            // Call the original function
            originalUpdateAccordionStatus.call(this, sectionId, status);

            // Then update the appearance
            updateAccordionItemAppearance();
        };

        // Also run it initially to set the correct state
        updateAccordionItemAppearance();
    }
    // // Update the submit button to check all sections
    // const submitClaimBtn = document.getElementById('submit-claim');
    // if (submitClaimBtn) {
    //     submitClaimBtn.addEventListener('click', function (event) {
    //         // Check if all required sections are complete
    //         if (window.ClaimAssistant && ClaimAssistant.accordionStatus) {
    //             ClaimAssistant.accordionStatus = {
    //                 ...ClaimAssistant.accordionStatus,
    //                 'collapseSeven': 'pending',
    //                 'collapseEight': 'pending',
    //                 'collapseNine': 'pending'
    //             };

    //             // Run the checkmark initialization for the new accordion sections
    //             if (typeof ClaimAssistant.initAccordionCheckmarks === 'function') {
    //                 ClaimAssistant.initAccordionCheckmarks();
    //             }

    //             // Get all accordion statuses
    //             const allComplete = Object.values(ClaimAssistant.accordionStatus).every(
    //                 status => status === 'done'
    //             );

    //             if (!allComplete) {
    //                 event.preventDefault();

    //                 // Find the first incomplete section
    //                 const incompleteSection = Object.keys(ClaimAssistant.accordionStatus).find(
    //                     key => ClaimAssistant.accordionStatus[key] !== 'done'
    //                 );

    //                 if (incompleteSection) {
    //                     // Open the incomplete section
    //                     const accordionButton = document.querySelector(`button[data-bs-target="#${incompleteSection}"]`);
    //                     if (accordionButton && accordionButton.classList.contains('collapsed')) {
    //                         accordionButton.click();
    //                     }

    //                     // Show an alert
    //                     alert('Please complete all sections before submitting your claim.');

    //                     // Scroll to the section
    //                     accordionButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
    //                 }
    //             }
    //         }
    //     });
    // }
    const submitClaimBtn = document.getElementById('submit-claim');
    const modalTitle = document.getElementById('verticallyCenterLabel');
    const modalBody = document.querySelector('#verticallyCenter .modal-body');
    const modalFooterSaveBtn = document.querySelector('#verticallyCenter .modal-footer .btn-secondary');
    const declarationCheck = document.getElementById('declarationCheck');

    // Function to validate all required fields
    function validateSubmission() {
        // Check if declaration checkbox is checked
        if (!declarationCheck.checked) {
            return {
                valid: false,
                message: "Please agree to the declaration before submitting the claim."
            };
        }

        // Client Name validation
        const clientName = document.getElementById('clientName');
        if (!clientName || !clientName.value.trim()) {
            return {
                valid: false,
                message: "Please enter a Client Name."
            };
        }

        // Policy Number validation
        const policyNumber = document.getElementById('policyNumber');
        if (!policyNumber || policyNumber.value === "") {
            return {
                valid: false,
                message: "Please select a Policy Number."
            };
        }

        // Vehicle Registration validation
        const vehicleRegistration = document.getElementById('vehicleRegistration');
        if (!vehicleRegistration || !vehicleRegistration.value.trim()) {
            return {
                valid: false,
                message: "Please enter a Vehicle Registration Number."
            };
        }

        // Driver Name validation
        const driverName = document.getElementById('driverName');
        if (!driverName || !driverName.value.trim()) {
            return {
                valid: false,
                message: "Please enter a Driver Name."
            };
        }

        // Incident Description validation
        const incidentDescription = document.getElementById('incidentDescription');
        if (!incidentDescription || !incidentDescription.value.trim()) {
            return {
                valid: false,
                message: "Please provide an Incident Description."
            };
        }

        // If all validations pass
        return {
            valid: true,
            message: "All required information has been provided. Your claim is ready to be submitted."
        };
    }

    // Update modal content based on validation
    function updateModal(validationResult) {
        if (validationResult.valid) {
            modalTitle.textContent = "Submit Claim";
            modalBody.innerHTML = `
                <div class="alert alert-success">
                    <i class="ph ph-check-circle me-2"></i>
                    ${validationResult.message}
                </div>
                <p>Claim Reference: <strong>CLM-${Math.floor(10000 + Math.random() * 90000)}</strong></p>
                <p>Submitted Date: <strong>${new Date().toLocaleDateString()}</strong></p>
            `;
            modalFooterSaveBtn.textContent = "Confirm Submission";
            modalFooterSaveBtn.classList.remove('btn-secondary');
            modalFooterSaveBtn.classList.add('btn-success');
        } else {
            modalTitle.textContent = "Cannot Submit Claim";
            modalBody.innerHTML = `
                <div class="alert alert-danger">
                    <i class="ph ph-warning-circle me-2"></i>
                    ${validationResult.message}
                </div>
                <p>Please complete all required information before submitting the claim.</p>
            `;
            modalFooterSaveBtn.textContent = "Close";
            modalFooterSaveBtn.classList.remove('btn-success');
            modalFooterSaveBtn.classList.add('btn-secondary');
        }
    }

    // Handle click on submit button (before modal opens)
    submitClaimBtn.addEventListener('click', function (event) {
        const validationResult = validateSubmission();
        updateModal(validationResult);
    });

    // Handle confirmation button in the modal
    modalFooterSaveBtn.addEventListener('click', function () {
        const validationResult = validateSubmission();

        if (validationResult.valid) {
            setTimeout(function () {
                window.location.href = "index.html";
            }, 1000);
        } else {
            // Close the modal if validation fails
            const modal = bootstrap.Modal.getInstance(document.getElementById('verticallyCenter'));
            modal.hide();

            // Optionally scroll to the first section with errors
            document.querySelector('.accordion-button.collapsed').click();
        }
    });
});